import React, { useEffect, useMemo, useRef, useState } from "react";
import { encodeMentions, initials } from "./taskUtils";

const MAX_SUGGESTIONS = 6;
const MAX_INPUT_HEIGHT = 160;

// Chat input with @mention autocomplete. Typing "@" followed by part of a
// name opens a people picker; picked people are encoded as mentions on submit.
// Enter sends, Shift+Enter inserts a new line.
export default function CommentComposer({ people, onSubmit }) {
  const [text, setText] = useState("");
  const [mentions, setMentions] = useState([]);
  const [query, setQuery] = useState(null); // { start, term } while typing a mention
  const [highlight, setHighlight] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const textareaRef = useRef(null);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, MAX_INPUT_HEIGHT)}px`;
  }, [text]);

  // The @ button inserts a trigger at the caret and opens the people picker.
  const startMention = () => {
    const el = textareaRef.current;
    const caret = el?.selectionStart ?? text.length;
    const needsSpace = caret > 0 && !/\s/.test(text[caret - 1]);
    const insert = `${needsSpace ? " " : ""}@`;
    const next = `${text.slice(0, caret)}${insert}${text.slice(caret)}`;
    const nextCaret = caret + insert.length;
    setText(next);
    setQuery({ start: nextCaret - 1, term: "" });
    setHighlight(0);
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(nextCaret, nextCaret);
    });
  };

  const suggestions = useMemo(() => {
    if (!query) return [];
    const term = query.term.toLowerCase();
    return people
      .filter(
        (p) =>
          p.name.toLowerCase().includes(term) ||
          p.email?.toLowerCase().includes(term),
      )
      .slice(0, MAX_SUGGESTIONS);
  }, [people, query]);

  const updateQuery = (value, caret) => {
    const before = value.slice(0, caret);
    const match = /(^|\s)@([^\s@]{0,30})$/.exec(before);
    if (match) {
      setQuery({ start: caret - match[2].length - 1, term: match[2] });
      setHighlight(0);
    } else {
      setQuery(null);
    }
  };

  const handleChange = (e) => {
    setText(e.target.value);
    updateQuery(e.target.value, e.target.selectionStart);
  };

  const pickPerson = (person) => {
    const caret = textareaRef.current?.selectionStart ?? text.length;
    const next = `${text.slice(0, query.start)}@${person.name} ${text.slice(caret)}`;
    setText(next);
    setMentions((prev) => [...prev, { id: person.id, name: person.name }]);
    setQuery(null);
    const nextCaret = query.start + person.name.length + 2;
    requestAnimationFrame(() => {
      textareaRef.current?.focus();
      textareaRef.current?.setSelectionRange(nextCaret, nextCaret);
    });
  };

  const submit = async () => {
    const trimmed = text.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    try {
      await onSubmit(encodeMentions(trimmed, mentions));
      setText("");
      setMentions([]);
      setQuery(null);
    } catch {
      // keep the draft so a failed send doesn't lose what was typed
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (query && suggestions.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlight((h) => (h + 1) % suggestions.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlight((h) => (h - 1 + suggestions.length) % suggestions.length);
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        pickPerson(suggestions[highlight]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setQuery(null);
        return;
      }
    }
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div className="tk-composer">
      <div className="tk-composer-box">
        {query && suggestions.length > 0 && (
          <ul className="tk-mention-menu" role="listbox">
            {suggestions.map((person, idx) => (
              <li key={person.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={idx === highlight}
                  className={idx === highlight ? "active" : ""}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    pickPerson(person);
                  }}
                >
                  <span className="tk-avatar tk-avatar-sm">
                    {initials(person.name)}
                  </span>
                  <span className="tk-mention-name">{person.name}</span>
                  <span className="tk-mention-email">{person.email}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
        <button
          type="button"
          className="tk-composer-icon"
          onMouseDown={(e) => e.preventDefault()}
          onClick={startMention}
          aria-label="Mention someone"
          title="Mention someone"
        >
          @
        </button>
        <textarea
          ref={textareaRef}
          value={text}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onClick={(e) => updateQuery(text, e.target.selectionStart)}
          onBlur={() => setQuery(null)}
          placeholder="Write a message…"
          rows={1}
          aria-label="Write a message"
        />
        <button
          type="button"
          className="tk-send-btn"
          onClick={submit}
          disabled={!text.trim() || submitting}
          aria-label="Send message"
          title="Send"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M4 12l16-8-6 16-2.5-6.5z" />
            <path d="M11.5 13.5L20 4" />
          </svg>
        </button>
      </div>
      <div className="tk-hint">
        Enter to send · Shift + Enter for a new line · @ to mention
      </div>
    </div>
  );
}
